import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../../firebase";
import { ResultPageConfig } from "../../../types/ResultPageData";
import { SummaryData } from "../../../types/SummaryData";
import { GroupData } from "../../../types/GroupData";

import {
  getAverageBySongs,
  getResultClassName,
  getTotalBySongs,
  getSortedGroups,
} from "../../../utils/scoreUtils";

const ENTRY_CLASS_MAP: Record<string, string> = {
  quartetSemiFinal: "QuartetContest",
  quartetFinal: "QuartetContest",
  chorusFinal: "ChorusContest",
  evaluationOnly: "EvaluationOnly",
};

interface ResultScoreProps {
  year: number;
  pageConfig: ResultPageConfig;
  finalistCount: number | null;
}

const COMPETITIONS = [
  {
    number: 1,
    name: "1.Intl Prelim Quartet Qualifier (JBA)",
  },
  {
    number: 2,
    name: "2.Japan Barbershop Convention Quartet Championship",
  },
  {
    number: 3,
    name: "2.Japan Barbershop Convention Chorus Championship",
  },
];

const formatDate = (value: any) => {
  if (!value) return null;

  const date =
    typeof value?.toDate === "function" ? value.toDate() : new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const Result: React.FC<ResultScoreProps> = ({
  year,
  pageConfig: currentPage,
  finalistCount: finalistCountProp,
}) => {
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [groups, setGroups] = useState<GroupData[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const targetEntryClass = ENTRY_CLASS_MAP[currentPage.type];

        // 年・エントリークラスが分かっているので、groupsコレクション全件ではなく
        // 該当する分だけをFirestore側で絞り込んで取得する
        // （このページは閲覧数が多いため、読み取り件数の削減を優先している）
        const start = new Date(`${year}-01-01T00:00:00`);
        const end = new Date(`${year + 1}-01-01T00:00:00`);

        const groupsQuery = targetEntryClass
          ? query(
              collection(db, "groups"),
              where("entryClass", "==", targetEntryClass),
              where("createdAt", ">=", start),
              where("createdAt", "<", end),
            )
          : null;

        const [summarySnap, groupsSnap] = await Promise.all([
          getDoc(doc(db, "summary", String(year))),
          groupsQuery ? getDocs(groupsQuery) : Promise.resolve(null),
        ]);

        const fetchedSummary = summarySnap.exists()
          ? (summarySnap.data() as SummaryData)
          : null;

        // isDeletedはFirestore側の範囲条件と一緒には使えないためここで絞り込む
        const fetchedGroups: GroupData[] = groupsSnap
          ? groupsSnap.docs
              .map((groupDoc) => ({
                ...(groupDoc.data() as GroupData),
                id: groupDoc.id,
              }))
              .filter((group) => !group.isDeleted)
          : [];

        setSummary(fetchedSummary);
        setGroups(getSortedGroups(fetchedGroups, {}, false, [1, 2, 3, 4]));
      } catch (error) {
        console.error("リザルトデータ取得エラー:", error);
      }
    };

    fetchData();
  }, [year, currentPage.type]);

  // -----------------------------
  // 会場・開催日
  // -----------------------------
  const getVenue = () => {
    if (!summary || !currentPage) {
      return null;
    }

    const venueMap: Record<
      string,
      {
        title: string;
        venue?: string;
        date?: any;
      }
    > = {
      quartetSemiFinal: {
        title: "Quartet SemiFinals",
        venue: summary.quartetSemiFinalVenue,
        date: summary.quartetSemiFinalAt,
      },

      quartetFinal: {
        title: "Quartet Finals",
        venue: summary.quartetFinalVenue,
        date: summary.quartetFinalAt,
      },

      chorusFinal: {
        title: "Chorus Contests",
        venue: summary.chorusFinalVenue,
        date: summary.chorusFinalAt,
      },

      evaluationOnly: {
        title: "Evaluation Only",
        venue: summary.evaluationOnlyVenue,
        date: summary.evaluationOnlyAt,
      },
    };

    return venueMap[currentPage.type] ?? null;
  };

  const venueData = getVenue();
  const formattedDate = formatDate(venueData?.date);

  // groupsは取得時点で該当の年・エントリークラス・isDeletedの絞り込み＋並び替え済み
  const displaySourceGroups = groups;

  const finalistCount = finalistCountProp ?? displaySourceGroups.length;

  const topGroups = displaySourceGroups.slice(0, finalistCount);

  const displayGroups =
    currentPage?.type === "quartetFinal"
      ? topGroups
      : currentPage?.type === "quartetSemiFinal"
        ? displaySourceGroups.slice(finalistCount)
        : currentPage?.type === "evaluationOnly"
          ? [...displaySourceGroups].sort(
              (a, b) => Number(a.entryNumber) - Number(b.entryNumber),
            )
          : displaySourceGroups;

  const finalGroups = topGroups
    .filter((group) => group.finalOrder != null)
    .sort((a, b) => Number(a.finalOrder) - Number(b.finalOrder));

  const mtGroup =
    displaySourceGroups.find(
      (group) =>
        group.finalOrder != null &&
        !topGroups.some((finalGroup) => finalGroup.id === group.id),
    ) ?? null;

  const getCompetitionLabel = (group: GroupData) => {
    const numbers: number[] = [];

    if (group.joinPreliminary) {
      numbers.push(1);
    }

    if (group.joinChampionship) {
      numbers.push(2);
    }

    if (numbers.length === 0) {
      return "";
    }

    return ` (${numbers.join(", ")})`;
  };

  const championshipGroup = displaySourceGroups.find(
    (group) => group.joinChampionship === true,
  );

  return (
    <div className="main">
      <div className="result-area">
        <h1>
          Official Scoring Summary
          <br />
          Japan Barbershop Convention {year} {venueData?.title}
          <br />
          <span>
            {venueData?.venue}; {formattedDate}
          </span>
        </h1>

        <table className="result-table">
          <thead>
            <tr>
              <th className="text-left head-group">Group</th>
              <th className="text-left">Songs</th>
              <th>MUS</th>
              <th>PER</th>
              <th>SNG</th>
              <th>Total</th>
            </tr>
          </thead>

          {displayGroups.map((group, index) => (
            <tbody
              key={group.id}
              className={index % 2 === 0 ? "group-even" : "group-odd"}
            >
              <tr className="tr-head">
                <td className="text-left font-bold">
                  <span className="runk-number">
                    {currentPage?.type === "quartetSemiFinal"
                      ? finalistCount + index + 1
                      : index + 1}
                  </span>
                  {group.groupName}
                  {getCompetitionLabel(group)}
                </td>

                {currentPage?.type === "quartetFinal" ? (
                  <>
                    <td className="text-left font-bold total-point">
                      {getTotalBySongs(group, {}, false, [1, 2, 3, 4])}
                    </td>

                    <td className="font-bold">
                      {getAverageBySongs(
                        group,
                        {},
                        false,
                        [1, 2, 3, 4],
                        ["Mus"],
                      )}
                    </td>

                    <td className="font-bold">
                      {getAverageBySongs(
                        group,
                        {},
                        false,
                        [1, 2, 3, 4],
                        ["Per"],
                      )}
                    </td>

                    <td className="font-bold">
                      {getAverageBySongs(
                        group,
                        {},
                        false,
                        [1, 2, 3, 4],
                        ["Sng"],
                      )}
                    </td>

                    <td className="font-bold">
                      {getAverageBySongs(group, {}, false, [1, 2, 3, 4])}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="text-left font-bold total-point">
                      {getTotalBySongs(group, {}, false, [1, 2])}
                    </td>

                    <td className="font-bold">
                      {getAverageBySongs(group, {}, false, [1, 2], ["Mus"])}
                    </td>

                    <td className="font-bold">
                      {getAverageBySongs(group, {}, false, [1, 2], ["Per"])}
                    </td>

                    <td className="font-bold">
                      {getAverageBySongs(group, {}, false, [1, 2], ["Sng"])}
                    </td>

                    <td className="font-bold">
                      {getAverageBySongs(group, {}, false, [1, 2])}
                    </td>
                  </>
                )}
              </tr>

              {currentPage?.type === "quartetFinal" && (
                <>
                  <tr className="tr-head">
                    <td></td>

                    <td className="text-left font-bold final-point">
                      {getTotalBySongs(group, {}, false, [3, 4])}
                    </td>

                    <td className="font-bold">
                      {getAverageBySongs(group, {}, false, [3, 4], ["Mus"])}
                    </td>

                    <td className="font-bold">
                      {getAverageBySongs(group, {}, false, [3, 4], ["Per"])}
                    </td>

                    <td className="font-bold">
                      {getAverageBySongs(group, {}, false, [3, 4], ["Sng"])}
                    </td>

                    <td className="font-bold">
                      {getAverageBySongs(group, {}, false, [3, 4])}
                    </td>
                  </tr>

                  <tr>
                    <td></td>

                    <td className="text-left">
                      {group.songName3}
                      <span className="arranged">{group.songArranged3}</span>
                    </td>

                    <td
                      className={getResultClassName(
                        "Mus",
                        "songPenaltyMus3",
                        false,
                        group,
                        {},
                      )}
                    >
                      {group.songResultMus3}
                    </td>

                    <td
                      className={getResultClassName(
                        "Per",
                        "songPenaltyPer3",
                        false,
                        group,
                        {},
                      )}
                    >
                      {group.songResultPer3}
                    </td>

                    <td
                      className={getResultClassName(
                        "Sng",
                        "songPenaltySng3",
                        false,
                        group,
                        {},
                      )}
                    >
                      {group.songResultSng3}
                    </td>

                    <td>{getAverageBySongs(group, {}, false, [3])}</td>
                  </tr>

                  <tr className="tr-foot">
                    <td></td>

                    <td className="text-left">
                      {group.songName4}
                      <span className="arranged">{group.songArranged4}</span>
                    </td>

                    <td
                      className={getResultClassName(
                        "Mus",
                        "songPenaltyMus4",
                        false,
                        group,
                        {},
                      )}
                    >
                      {group.songResultMus4}
                    </td>

                    <td
                      className={getResultClassName(
                        "Per",
                        "songPenaltyPer4",
                        false,
                        group,
                        {},
                      )}
                    >
                      {group.songResultPer4}
                    </td>

                    <td
                      className={getResultClassName(
                        "Sng",
                        "songPenaltySng4",
                        false,
                        group,
                        {},
                      )}
                    >
                      {group.songResultSng4}
                    </td>

                    <td>{getAverageBySongs(group, {}, false, [4])}</td>
                  </tr>
                </>
              )}
              {currentPage?.type === "quartetFinal" && (
                <tr className="tr-head">
                  <td></td>

                  <td className="text-left font-bold semifinal-point">
                    {getTotalBySongs(group, {}, false, [1, 2])}
                  </td>

                  <td className="font-bold">
                    {getAverageBySongs(group, {}, false, [1, 2], ["Mus"])}
                  </td>

                  <td className="font-bold">
                    {getAverageBySongs(group, {}, false, [1, 2], ["Per"])}
                  </td>

                  <td className="font-bold">
                    {getAverageBySongs(group, {}, false, [1, 2], ["Sng"])}
                  </td>

                  <td className="font-bold">
                    {getAverageBySongs(group, {}, false, [1, 2])}
                  </td>
                </tr>
              )}

              <tr>
                <td></td>

                <td className="text-left">
                  {group.songName1}
                  <span className="arranged">{group.songArranged1}</span>
                </td>

                <td
                  className={getResultClassName(
                    "Mus",
                    "songPenaltyMus1",
                    false,
                    group,
                    {},
                  )}
                >
                  {group.songResultMus1}
                </td>

                <td
                  className={getResultClassName(
                    "Per",
                    "songPenaltyPer1",
                    false,
                    group,
                    {},
                  )}
                >
                  {group.songResultPer1}
                </td>

                <td
                  className={getResultClassName(
                    "Sng",
                    "songPenaltySng1",
                    false,
                    group,
                    {},
                  )}
                >
                  {group.songResultSng1}
                </td>

                <td>{getAverageBySongs(group, {}, false, [1])}</td>
              </tr>

              <tr className="tr-foot">
                <td></td>

                <td className="text-left">
                  {group.songName2}
                  <span className="arranged">{group.songArranged2}</span>
                </td>

                <td
                  className={getResultClassName(
                    "Mus",
                    "songPenaltyMus2",
                    false,
                    group,
                    {},
                  )}
                >
                  {group.songResultMus2}
                </td>

                <td
                  className={getResultClassName(
                    "Per",
                    "songPenaltyPer2",
                    false,
                    group,
                    {},
                  )}
                >
                  {group.songResultPer2}
                </td>

                <td
                  className={getResultClassName(
                    "Sng",
                    "songPenaltySng2",
                    false,
                    group,
                    {},
                  )}
                >
                  {group.songResultSng2}
                </td>

                <td>{getAverageBySongs(group, {}, false, [2])}</td>
              </tr>
            </tbody>
          ))}
        </table>

        <div className="info">
          <p>
            Groups are listed above by total points and in accordance with
            Article VII of the BHS Contest Rules.
          </p>
        </div>

        {/* Draw */}
        {currentPage?.type === "quartetSemiFinal" && (
          <div className="info">
            <h2>Draw</h2>

            {finalGroups.map((group, index) => (
              <span key={group.id}>
                <span className="info-font-bold">{index + 1}:</span>{" "}
                <span className="draw-span">{group.groupName}</span>
              </span>
            ))}

            {mtGroup && (
              <>
                <span className="info-font-bold">MT:</span>

                <span className="draw-span">{mtGroup.groupName}</span>
              </>
            )}

            <p>
              Singing Order in Finals: MT &gt; 1. &gt; 2. &gt; … &gt;{" "}
              {finalistCount}.
            </p>
          </div>
        )}

        {/* Awards - Quartet Final 2026 */}
        {currentPage?.type === "quartetFinal" && year === 2026 && (
          <div className="info">
            <h2>Awards</h2>

            <p className="font-bold">
              {COMPETITIONS[0].name}:
              <span className="font-normal">
                {" "}
                (Result determined post-contest)
              </span>
            </p>

            <p className="font-bold">
              {COMPETITIONS[1].name}:
              <span className="font-normal">
                {" "}
                {championshipGroup?.groupName ?? "-"}
              </span>
            </p>
          </div>
        )}

        {/* Awards - Chorus Final 2026 */}
        {currentPage?.type === "chorusFinal" && year === 2026 && (
          <div className="info">
            <h2>Awards</h2>

            <p className="font-bold">
              {COMPETITIONS[0].name}:
              <span className="font-normal">
                {" "}
                (Result determined post-contest)
              </span>
            </p>

            <p className="font-bold">
              {COMPETITIONS[2].name}:
              <span className="font-normal">
                {" "}
                {championshipGroup?.groupName ?? "-"}
              </span>
            </p>
          </div>
        )}

        {/* Awards - Quartet Final 2025 */}
        {currentPage?.type === "quartetFinal" && year === 2025 && (
          <div className="info">
            <h2>Awards</h2>

            <p className="font-bold">
              Japan Quartet Championship:
              <span className="font-normal">
                {" "}
                {displaySourceGroups[0]?.groupName ?? "-"}
              </span>
            </p>
          </div>
        )}

        {/* Panel */}
        {summary &&
          (summary.judgePanelADM ||
            summary.judgePanelMUS ||
            summary.judgePanelPER ||
            summary.judgePanelSNG) && (
            <div className="info">
              <h2>Panel</h2>

              {summary.judgePanelADM && <p>ADM: {summary.judgePanelADM}</p>}

              {summary.judgePanelMUS && <p>MUS: {summary.judgePanelMUS}</p>}

              {summary.judgePanelPER && <p>PER: {summary.judgePanelPER}</p>}

              {summary.judgePanelSNG && <p>SNG: {summary.judgePanelSNG}</p>}
            </div>
          )}
      </div>
    </div>
  );
};

export default Result;
